import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * useCollapseOnScroll
 * -------------------
 * Tells the page when to remove its "chrome" (tab selector, header text,
 * action buttons) so the scrollable list can take their space.
 *
 *   scroll down            -> isCollapsed = true   (render nothing for the chrome)
 *   scroll up              -> isCollapsed = false
 *   reach top / near end   -> isCollapsed = false
 *
 * Removing nodes from the DOM resizes the scroll container, and a resized
 * container produces scroll events of its own (the browser clamps scrollTop).
 * Those synthetic events look exactly like user input and are what causes the
 * classic "hide -> show -> hide" flicker, especially at the bottom of a list.
 * The protections below exist to break that loop:
 *
 *  1. Settle window   After every toggle, scroll input is ignored for `settleMs`
 *                     and the scroll baseline is re-read afterwards.
 *  2. Look-ahead      The hook measures how many px the list gains when
 *                     collapsed and refuses to collapse if that would put the
 *                     user inside the bottom zone (or leave nothing to scroll).
 *  3. Hysteresis      Collapsing needs more room below than expanding does
 *                     (`bottomOffset + collapseMargin` vs `bottomOffset`), and
 *                     the top zone uses different thresholds for each direction.
 *  4. Thresholds      Direction changes only count after `collapseDelta` /
 *                     `expandDelta` px of accumulated movement; tiny
 *                     counter-movements (finger jitter) are ignored.
 *  5. Trailing check  When the settle window ends the position is re-checked,
 *                     so a fling that finished mid-transition still ends up
 *                     expanded at the bottom.
 *  6. Safety net      If the list stops overflowing while collapsed (data
 *                     refetch, rotation) the chrome comes back, otherwise the
 *                     user would have no way to scroll it back.
 *
 * Usage:
 *   const {scrollRef, isCollapsed, expand} = useCollapseOnScroll({resetKey: tab});
 *   <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">...</div>
 *   {!isCollapsed && <Actions />}
 */

export interface UseCollapseOnScrollOptions {
  /** Accumulated downward scroll (px) needed before collapsing. */
  collapseDelta?: number;
  /** Accumulated upward scroll (px) needed before expanding. */
  expandDelta?: number;
  /** Never collapse before the list is scrolled past this many px. Expands again at half of it. */
  topOffset?: number;
  /** Within this many px of the end of the list the chrome is always shown. */
  bottomOffset?: number;
  /** Extra room (px) that must remain below the user after collapsing, on top of `bottomOffset`. */
  collapseMargin?: number;
  /** How long (ms) scroll input is ignored after the layout changes. */
  settleMs?: number;
  /** Height (px) the list gains when collapsed. Only used until the first real measurement. */
  fallbackGain?: number;
  /** When this value changes (e.g. the active tab) the hook expands and scrolls to the top. */
  resetKey?: unknown;
}

export interface UseCollapseOnScrollResult {
  /** Attach to the element that actually scrolls. Works with elements that mount late. */
  scrollRef: (node: HTMLElement | null) => void;
  /** True while the chrome should be removed from the DOM. */
  isCollapsed: boolean;
  /** Bring the chrome back immediately (e.g. when the user selects something). */
  expand: () => void;
  /** Expand and jump back to the top of the list. */
  reset: () => void;
}

/** Movements smaller than this against the current direction are treated as noise. */
const JITTER_PX = 4;

const readScrollTop = (el: HTMLElement) =>
  Math.min(
    Math.max(el.scrollTop, 0), // iOS rubber-banding can report negative values
    Math.max(el.scrollHeight - el.clientHeight, 0), // ...or values past the end
  );

const useCollapseOnScroll = (
  options: UseCollapseOnScrollOptions = {},
): UseCollapseOnScrollResult => {
  const {
    collapseDelta = 24,
    expandDelta = 16,
    topOffset = 48,
    bottomOffset = 64,
    collapseMargin = 64,
    settleMs = 350,
    fallbackGain = 180,
    resetKey,
  } = options;

  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const elementRef = useRef<HTMLElement | null>(null);
  const collapsedRef = useRef(false);
  const lastTopRef = useRef(0);
  const accumulatedRef = useRef(0);
  const lockUntilRef = useRef(0);
  const gainRef = useRef(fallbackGain);
  const heightBeforeToggleRef = useRef<number | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const settleRef = useRef<() => void>(() => {});

  // Latest options, readable from long-lived listeners without re-subscribing.
  const configRef = useRef({
    collapseDelta,
    expandDelta,
    topOffset,
    bottomOffset,
    collapseMargin,
    settleMs,
  });

  useEffect(() => {
    configRef.current = {
      collapseDelta,
      expandDelta,
      topOffset,
      bottomOffset,
      collapseMargin,
      settleMs,
    };
  });

  // Callback ref: the scroller may not exist on first render (page loaders,
  // conditional branches), so the listener is attached when it actually mounts.
  const scrollRef = useCallback((node: HTMLElement | null) => {
    elementRef.current = node;
    setElement(node);
  }, []);

  // The single place where the collapsed state changes.
  const setCollapsed = useCallback((next: boolean) => {
    if (collapsedRef.current === next) return;

    const {settleMs: settle} = configRef.current;

    collapsedRef.current = next;
    accumulatedRef.current = 0;
    heightBeforeToggleRef.current = elementRef.current
      ? elementRef.current.clientHeight
      : null;
    lockUntilRef.current = performance.now() + settle;

    if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => settleRef.current(), settle + 20);

    setIsCollapsed(next);
  }, []);

  // Position-only rules for the collapsed state (no direction involved).
  const syncWithPosition = useCallback(() => {
    const el = elementRef.current;

    if (!el || !collapsedRef.current) return;
    if (performance.now() < lockUntilRef.current) return;

    const {topOffset: top0, bottomOffset: bottom0} = configRef.current;
    const top = readScrollTop(el);
    const maxTop = el.scrollHeight - el.clientHeight;

    const atTop = top <= top0 / 2;
    // Also true for lists that no longer overflow (maxTop <= bottomOffset).
    const atBottom = maxTop - top <= bottom0;

    if (atTop || atBottom) setCollapsed(false);
  }, [setCollapsed]);

  // Runs once the layout has had time to settle after a toggle.
  useEffect(() => {
    settleRef.current = () => {
      const el = elementRef.current;

      if (!el) return;

      // Learn how much height the toggle really moved, for the look-ahead.
      const before = heightBeforeToggleRef.current;

      if (before !== null) {
        const moved = Math.abs(el.clientHeight - before);

        if (moved > 0) gainRef.current = moved;

        heightBeforeToggleRef.current = null;
      }

      // Re-baseline, so layout-induced scroll jumps never count as input.
      lastTopRef.current = readScrollTop(el);
      accumulatedRef.current = 0;

      syncWithPosition();
    };
  }, [syncWithPosition]);

  // Scroll listener.
  useEffect(() => {
    if (!element) return;

    lastTopRef.current = readScrollTop(element);
    accumulatedRef.current = 0;

    let scrollFrame: number | null = null;
    let resizeFrame: number | null = null;

    const evaluate = () => {
      scrollFrame = null;

      const cfg = configRef.current;
      const top = readScrollTop(element);
      const delta = top - lastTopRef.current;

      lastTopRef.current = top;

      // Layout is settling after a toggle: these scrolls are not the user's.
      if (performance.now() < lockUntilRef.current) {
        accumulatedRef.current = 0;
        return;
      }

      // Accumulate movement per direction, ignoring jitter against it.
      const flipped = accumulatedRef.current * delta < 0;

      if (flipped && Math.abs(delta) < JITTER_PX) return;

      accumulatedRef.current = flipped ? delta : accumulatedRef.current + delta;

      const accumulated = accumulatedRef.current;
      const distanceToBottom =
        element.scrollHeight - element.clientHeight - top;

      if (collapsedRef.current) {
        const scrolledUp = accumulated <= -cfg.expandDelta;
        const atTop = top <= cfg.topOffset / 2;
        const nearBottom = distanceToBottom <= cfg.bottomOffset;

        if (scrolledUp || atTop || nearBottom) setCollapsed(false);

        return;
      }

      if (accumulated < cfg.collapseDelta || top <= cfg.topOffset) return;

      // Look-ahead: collapsing makes the list `gain` px taller, which eats
      // into the room left below. Don't collapse if that lands us at the end.
      const roomAfterCollapse = distanceToBottom - gainRef.current;

      if (roomAfterCollapse <= cfg.bottomOffset + cfg.collapseMargin) return;

      setCollapsed(true);
    };

    const onScroll = () => {
      if (scrollFrame === null) scrollFrame = requestAnimationFrame(evaluate);
    };

    // Rotation / keyboard / data changes. Deferred a frame to avoid RO loop warnings.
    const observer = new ResizeObserver(() => {
      if (resizeFrame !== null) cancelAnimationFrame(resizeFrame);

      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        syncWithPosition();
      });
    });

    element.addEventListener('scroll', onScroll, {passive: true});
    observer.observe(element);

    return () => {
      element.removeEventListener('scroll', onScroll);
      observer.disconnect();

      if (scrollFrame !== null) cancelAnimationFrame(scrollFrame);
      if (resizeFrame !== null) cancelAnimationFrame(resizeFrame);
    };
  }, [element, setCollapsed, syncWithPosition]);

  // Content can change under a collapsed list (refetch, pagination).
  // Cheap: returns immediately unless collapsed.
  useEffect(() => {
    syncWithPosition();
  });

  useEffect(
    () => () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    },
    [],
  );

  const expand = useCallback(() => setCollapsed(false), [setCollapsed]);

  const reset = useCallback(() => {
    setCollapsed(false);

    if (elementRef.current) elementRef.current.scrollTop = 0;

    lastTopRef.current = 0;
    accumulatedRef.current = 0;
  }, [setCollapsed]);

  useEffect(() => {
    reset();
  }, [resetKey, reset]);

  return {scrollRef, isCollapsed, expand, reset};
};

export default useCollapseOnScroll;
